import { GoogleMapsAPIWrapper } from '@agm/core';
import * as mapTypes from '@agm/core/services/google-maps-types';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Unsubscriber } from '@xpo/ngx-ltl';
import { union as _union } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GlobalFilterMapCoordinate } from '../../../..';
import {
  PndStoreState,
  UnassignedDeliveriesStoreActions,
  UnassignedPickupsStoreActions,
} from '../../../../../../store';
import { EventItem, UnassignedDeliveryIdentifier } from '../../../../interfaces/event-item.interface';
import { MapToolbarService } from '../../../../services';

@Component({
  selector: 'pnd-geo-filter-polygon-selection',
  templateUrl: './geo-filter-polygon-selection.component.html',
  styleUrls: ['./geo-filter-polygon-selection.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeoFilterPolygonSelectionComponent implements OnDestroy, AfterViewInit {
  @Output()
  toggleDrawMode = new EventEmitter<boolean>();

  markers: EventItem<UnassignedDeliveryIdentifier>[] = [];
  inDrawMode = false;

  private disabledSubject = new BehaviorSubject<boolean>(false);
  readonly disabled$ = this.disabledSubject.asObservable();

  private googleMap: google.maps.Map;
  private polygonDrawnEventListener: google.maps.MapsEventListener;
  private newShape: google.maps.Polyline;
  private mouseMoveDrawer: mapTypes.MapsEventListener;
  private mouseUpDrawer: mapTypes.MapsEventListener;
  private mouseDownDrawer: mapTypes.MapsEventListener;
  private unsubscriber = new Unsubscriber();
  private drawing: boolean;

  constructor(
    private googleMapsApi: GoogleMapsAPIWrapper,
    private mapToolbarService: MapToolbarService,
    private pndStore$: Store<PndStoreState.State>
  ) {}

  @HostListener('document:keyup', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.inDrawMode) {
      this.togglePolygonDraw();
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber.complete();
    if (this.polygonDrawnEventListener) {
      google.maps.event.removeListener(this.polygonDrawnEventListener);
    }
    if (this.googleMap) {
      google.maps.event.clearListeners(this.googleMap.getDiv(), 'mousedown');
    }
  }

  ngAfterViewInit(): void {
    this.googleMapsApi.getNativeMap().then((map) => {
      if (map) {
        this.googleMap = (map as any) as google.maps.Map;
      }
    });
    this.mapToolbarService.setDrawModeState$.pipe(takeUntil(this.unsubscriber.done)).subscribe((enabled) => {
      if (this.inDrawMode && !enabled) {
        this.togglePolygonDraw();
      }
      this.disabledSubject.next(!enabled);
    });
    this.mapToolbarService.toggleDrawModeOff$.pipe(takeUntil(this.unsubscriber.done)).subscribe(() => {
      if (this.inDrawMode) {
        this.togglePolygonDraw();
      }
    });
  }

  togglePolygonDraw(): void {
    this.inDrawMode = !this.inDrawMode;
    this.toggleDrawMode.emit(this.inDrawMode);
    if (this.inDrawMode) {
      // this.mapToolbarService.clearPolygonLayer(this.googleMap, 'geoFilterLayer');
      this.mouseDownDrawer = google.maps.event.addListener(this.googleMap, 'mousedown', () => {
        if (this.inDrawMode && !this.drawing) {
          this.disableMap();
          this.initPolygonSelection();
        }
      });
    } else {
      google.maps.event.removeListener(this.mouseDownDrawer);
    }
  }

  private initPolygonSelection(): void {
    const polylineOptions: google.maps.PolylineOptions = {
      map: this.googleMap,
      strokeColor: '#304FFE',
      strokeWeight: 3,
      clickable: false,
      zIndex: 1,
      editable: false,
    };

    this.newShape = new google.maps.Polyline(polylineOptions);
    this.mouseMoveDrawer = google.maps.event.addListener(this.googleMap, 'mousemove', ($event) => {
      this.newShape.getPath().push($event.latLng);
    });
    this.mouseUpDrawer = google.maps.event.addListener(this.googleMap, 'mouseup', () => {
      google.maps.event.removeListener(this.mouseMoveDrawer);

      const path = this.newShape.getPath();

      this.newShape.setMap(null);
      const arrayofLatLng = path.getArray();
      const arrayForPolygontoUse = this.reducerDP(arrayofLatLng, 300);

      const reducedLatLonArray = arrayForPolygontoUse.map((point) => {
        return { lat: point.lat(), lon: point.lng() };
      });

      this.mapToolbarService.drawPolygonLayer(this.googleMap, arrayofLatLng, 'geoFilterLayer');

      this.onPolygonDrawn(reducedLatLonArray);
    });
  }

  private disableMap() {
    this.drawing = true;
    this.googleMap.setOptions({
      draggable: false,
    });
  }

  private enableMap() {
    this.drawing = false;
    this.googleMap.setOptions({
      draggable: true,
    });
  }

  private onPolygonDrawn(latLng: GlobalFilterMapCoordinate[]): void {
    this.pndStore$.dispatch(new UnassignedDeliveriesStoreActions.SetBoundingSearchArea({ boundingSearchArea: latLng }));
    this.pndStore$.dispatch(
      new UnassignedPickupsStoreActions.SetBoundingSearchAreaAction({ boundingSearchArea: latLng })
    );
    google.maps.event.removeListener(this.mouseUpDrawer);
    this.enableMap();
    this.togglePolygonDraw();
  }

  /* Stack-based Douglas Peucker line simplification routine
  https://en.wikipedia.org/wiki/Ramer–Douglas–Peucker_algorithm
   returned is a reduced GLatLng array
  */

  private reducerDP(source: google.maps.LatLng[], kink: number) {
    /* source Input coordinates in GLatLngs 	*/
    /* kink	in metres, kinks above this depth kept  */
    /* kink depth is the height of the triangle abc where a-b and b-c are two consecutive line segments */

    let nSource: number,
      nStack: number,
      nDest: number,
      start: number,
      end: number,
      i: number,
      sig: number,
      devSqr: number,
      maxDevSqr: number,
      bandSqr: number,
      kain12: number,
      y12: number,
      d12: number,
      x13: number,
      y13: number,
      d13: number,
      x23: number,
      y23: number,
      d23: number;
    const F = (Math.PI / 180.0) * 0.5;
    const index = new Array();
    const sigStart = new Array();
    const sigEnd = new Array();

    if (source.length < 3) {
      return source;
    }

    nSource = source.length;
    bandSqr = (kink * 360.0) / (2.0 * Math.PI * 6378137.0); /* Now in degrees */
    bandSqr *= bandSqr;
    nDest = 0;
    sigStart[0] = 0;
    sigEnd[0] = nSource - 1;
    nStack = 1;

    /* while the stack is not empty  ... */
    while (nStack > 0) {
      start = sigStart[nStack - 1];
      end = sigEnd[nStack - 1];
      nStack--;

      if (end - start > 1) {
        /* any intermediate points ? */
        /* ... yes, so find most deviant intermediate point to
          either side of line joining start & end points */

        kain12 = source[end].lng() - source[start].lng();
        y12 = source[end].lat() - source[start].lat();

        if (Math.abs(kain12) > 180.0) {
          kain12 = 360.0 - Math.abs(kain12);
        }

        kain12 *= Math.cos(F * (source[end].lat() + source[start].lat()));
        /* use avg lat to reduce lng */
        d12 = kain12 * kain12 + y12 * y12;

        for (i = start + 1, sig = start, maxDevSqr = -1.0; i < end; i++) {
          x13 = source[i].lng() - source[start].lng();
          y13 = source[i].lat() - source[start].lat();
          if (Math.abs(x13) > 180.0) {
            x13 = 360.0 - Math.abs(x13);
          }
          x13 *= Math.cos(F * (source[i].lat() + source[start].lat()));
          d13 = x13 * x13 + y13 * y13;

          x23 = source[i].lng() - source[end].lng();
          y23 = source[i].lat() - source[end].lat();
          if (Math.abs(x23) > 180.0) {
            x23 = 360.0 - Math.abs(x23);
          }

          x23 *= Math.cos(F * (source[i].lat() + source[end].lat()));
          d23 = x23 * x23 + y23 * y23;

          if (d13 >= d12 + d23) {
            devSqr = d23;
          } else if (d23 >= d12 + d13) {
            devSqr = d13;
          } else {
            devSqr = ((x13 * y12 - y13 * kain12) * (x13 * y12 - y13 * kain12)) / d12; // solve triangle
          }

          if (devSqr > maxDevSqr) {
            sig = i;
            maxDevSqr = devSqr;
          }
        }

        if (maxDevSqr < bandSqr) {
          /* is there a sig. intermediate point ? */
          /* ... no, so transfer current start point */
          index[nDest] = start;
          nDest++;
        } else {
          /* ... yes, so push two sub-sections on stack for further processing */
          nStack++;
          sigStart[nStack - 1] = sig;
          sigEnd[nStack - 1] = end;
          nStack++;
          sigStart[nStack - 1] = start;
          sigEnd[nStack - 1] = sig;
        }
      } else {
        /* ... no intermediate points, so transfer current start point */
        index[nDest] = start;
        nDest++;
      }
    }

    /* transfer last point */
    index[nDest] = nSource - 1;
    nDest++;

    /* make return array */
    const r = new Array();
    for (let ix = 0; ix < nDest; ix++) {
      r.push(source[index[ix]]);
    }
    return r;
  }
}
